import { type UseQueryResult, useQuery } from "react-query"

import axios, { AxiosError, type AxiosResponse } from "axios"
import { jsonrepair } from "jsonrepair"
import Papa from "papaparse"

import { FILE_SIZE_LIMIT_IN_BYTES } from "../../FileDetailsView/Constants"

export function parseFileContent({
  data,
  fileExtension,
  tab,
  setErrorMsg,
  formateJsonIntoTableData,
}: {
  data: string
  fileExtension?: string
  tab: "table" | "json"
  setErrorMsg: (msg: string) => void
  formateJsonIntoTableData: (jsonData: Record<PropertyKey, unknown>[]) => {
    formattedJsonData: Record<PropertyKey, string>[]
    tableColumnKeys: (string | number)[]
  }
}): ReturnType<typeof formateJsonIntoTableData> | string | Record<string, string>[] | void {
  if (fileExtension === "csv") {
    return Papa.parse<Record<string, string>>(data.trim(), {
      header: true,
      transform: (value) => (value === "" ? "__" : value),
    }).data
  }

  if (fileExtension === "json") {
    try {
      const json = typeof data === "string" ? JSON.parse(jsonrepair(data)) : data

      if (!Array.isArray(json)) {
        setErrorMsg("File is not in Json Array format")
        return
      }

      json.pop() // remove possibly incomplete last element

      if (tab === "table") {
        return formateJsonIntoTableData(json)
      }

      return JSON.stringify(json, null, "\t")
    } catch {
      setErrorMsg("File Cannot be viewed")
    }
  }
}

export function useFileContent({
  fileData,
  tab,
  setErrorMsg,
  formateJsonIntoTableData,
}: {
  fileData?: {
    fileUrl?: string
    fileExtension?: string
  }
  tab: "table" | "json" // or whatever values you expect
  setErrorMsg: (msg: string) => void
  formateJsonIntoTableData: (jsonData: Record<PropertyKey, unknown>[]) => {
    formattedJsonData: Record<PropertyKey, string>[]
    tableColumnKeys: (string | number)[]
  }
}): UseQueryResult {
  return useQuery<
    AxiosResponse<string>,
    AxiosError,
    ReturnType<typeof formateJsonIntoTableData> | string | Record<string, string>[] | void
  >({
    queryKey: ["data-file-content", fileData?.fileUrl],
    queryFn: () =>
      axios.get(fileData?.fileUrl as string, {
        headers: {
          Range: `bytes=0-${FILE_SIZE_LIMIT_IN_BYTES}`,
        },
      }),
    select: ({ data }) =>
      parseFileContent({
        data,
        fileExtension: fileData?.fileExtension,
        tab,
        setErrorMsg,
        formateJsonIntoTableData,
      }),
    enabled: !!fileData?.fileUrl,
  })
}
